import { Box, Stack } from "@mui/material";

import DetailedViewPageHeader from "@/App/Layouts/PageHeader/DetailedViewPageHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import { ROUTE_PATH } from "@/common/constants/routing";
import { useOnboardingContext } from "@/common/contexts/onboarding";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import { useUserProfile } from "@/common/hooks/user/useUserProfile";
import { ViewVariant } from "@/common/ts/layout";
import { appCommonConfig } from "@/config/config";
import { EntityType, OnboardingDto } from "@/core/api/generated/v1";

import AppButton from "../../../Button/AppButton";
import AppIcon from "../../../Icons/AppIcon";
import { AppModalCloseIconButton } from "../../../Modals/AppModalCloseIconButton";
import AppTypography from "../../../Text/AppTypography";
import BaseEntityView, { BaseEntityViewInheritableProps } from "../../components/BaseEntityView";
import { OnboardingIsShowOnLaunchInput } from "../OnboardingIsShowOnLaunchInput";

const defaultDisplayProps = {
  breadcrumbs: true,
  header: true,
  actions: true,
  closeInHeader: true,
  viewVariant: ViewVariant.Page,
};

interface Props extends BaseEntityViewInheritableProps<OnboardingDto> {
  displayProps?: Partial<typeof defaultDisplayProps>;
}

export function OnboardingIntroView({ displayProps, onClose }: Props) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const history = useAppHistory();
  const profile = useUserProfile();

  const onboardingContext = useOnboardingContext();

  return (
    <BaseEntityView
      entityType={EntityType.Onboarding}
      entityId={onboardingContext.onboarding?.id}
      entity={onboardingContext.onboarding}
      entityRequest={undefined}
      isIniting={onboardingContext.isIniting || !onboardingContext.onboarding}
    >
      {onboardingContext.onboarding && (
        <ViewLayout
          displayProps={displayProps}
          header={
            displayProps?.header && (
              <>
                <DetailedViewPageHeader
                  image={undefined}
                  title={
                    <>
                      Welcome{profile?.personName?.name ? <>, {profile?.personName?.name}</> : null}
                      !
                    </>
                  }
                  primaryActions={
                    displayProps?.actions && (
                      <Stack direction='row' sx={{ alignItems: "center" }}>
                        <OnboardingIsShowOnLaunchInput />
                      </Stack>
                    )
                  }
                  closeAction={
                    displayProps.closeInHeader && onClose ? (
                      <AppModalCloseIconButton onClick={onClose} />
                    ) : undefined
                  }
                />
              </>
            )
          }
        >
          <ViewContentBlock
            sx={{
              minHeight: "100%",
              flex: 1,
              borderRadius: (th) => th.shapeCustom.borderRadiusCardLarge,
            }}
          >
            <Stack
              sx={{ minHeight: "100%", flex: 1, justifyContent: "center", alignItems: "center" }}
            >
              <Stack sx={{ maxWidth: 780, alignItems: "center", textAlign: "center" }}>
                <AppTypography sx={{ fontWeight: 600 }} variant='h3'>
                  Getting started with{" "}
                  <Box sx={{ color: (th) => th.palette.primary.main }} component='span'>
                    {appCommonConfig.appName}
                  </Box>{" "}
                  is fast, intuitive, & pretty exciting.
                </AppTypography>

                <AppTypography variant='h6' sx={{ mt: 3 }}>
                  <Box sx={{ fontWeight: 400 }} component='span'>
                    In just a few steps, your rental or leasing operation becomes
                  </Box>{" "}
                  <Box sx={{ fontWeight: 600 }} component='span'>
                    fully digital, organised, and ready for smooth workflows
                  </Box>
                  .
                </AppTypography>

                <AppButton
                  sx={{
                    mt: 6,
                    minWidth: 220,
                  }}
                  variant='contained'
                  size='large'
                  color='primary'
                  endIcon={<AppIcon of='arrowForward' />}
                  onClick={async () => {
                    await onboardingContext.start();
                    history.push(ROUTE_PATH.ONBOARDING_VIEW_STEPS());
                  }}
                >
                  {`Let's start!`}
                </AppButton>
              </Stack>
            </Stack>
          </ViewContentBlock>
        </ViewLayout>
      )}
    </BaseEntityView>
  );
}
